import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Package, DollarSign, Users, CalendarCheck, 
  ShoppingCart, Utensils, Calendar, MessageSquare, Filter,
  Truck, BarChart3, MapPin, Clock, Trash2, Plus
} from "lucide-react";
import { PopUpEventManager } from "../components/PopUpEventManager";
import { FoodTruckScheduleManager } from "../components/FoodTruckScheduleManager";
import { AnalyticsPanel } from "../components/AnalyticsPanel";
import { MenuItemManager } from "../components/MenuItemManager";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [cateringInquiries, setCateringInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderFilter, setOrderFilter] = useState({
    status: '',
    date: '',
    search: ''
  });

  const fetchDashboardData = useCallback(async () => {
    try {
      const [statsRes, ordersRes, menuRes, cateringRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/orders`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/menu-items`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/catering-inquiries`, { withCredentials: true })
      ]);

      setStats(statsRes.data);
      setOrders(ordersRes.data);
      setMenuItems(menuRes.data);
      setCateringInquiries(cateringRes.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/orders/${orderId}`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success("Order status updated");
      fetchDashboardData();
    } catch (error) {
      toast.error("Failed to update order");
    }
  };

  const updateCateringStatus = async (inquiryId, newStatus) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/catering-inquiries/${inquiryId}`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success("Inquiry status updated");
      fetchDashboardData();
    } catch (error) {
      toast.error("Failed to update inquiry");
    }
  };

  const syncSquareRefunds = async (orderId) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/payments/square/sync-refunds/${orderId}`,
        {},
        { withCredentials: true }
      );
      if (data.synced) {
        toast.success(
          `Synced ${data.refund_count} refund(s). Status: ${data.order_status}`
        );
      } else {
        toast.info(data.message || "No refunds found in Square for this order.");
      }
      fetchDashboardData();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to sync with Square"
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-28 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const statusColors = {
    pending: "bg-yellow-100 text-yellow-800",
    confirmed: "bg-blue-100 text-blue-800",
    preparing: "bg-purple-100 text-purple-800",
    ready: "bg-green-100 text-green-800",
    delivered: "bg-emerald-100 text-emerald-800",
    picked_up: "bg-emerald-100 text-emerald-800",
    cancelled: "bg-red-100 text-red-800",
    refunded: "bg-red-100 text-red-800",
    partial_refund: "bg-orange-100 text-orange-800"
  };

  return (
    <div className="min-h-screen pt-28 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-stone-50 to-white">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">Manage your meal prep business</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-2 hover:border-emerald-300 transition-colors">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Orders</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.total_orders || 0}</p>
                </div>
                <div className="bg-emerald-100 p-3 rounded-full">
                  <ShoppingCart className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 hover:border-emerald-300 transition-colors">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Pending Orders</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.pending_orders || 0}</p>
                </div>
                <div className="bg-yellow-100 p-3 rounded-full">
                  <Package className="h-6 w-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 hover:border-emerald-300 transition-colors">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Revenue (30d)</p>
                  <p className="text-3xl font-bold text-gray-900">${stats?.revenue_30_days || 0}</p>
                </div>
                <div className="bg-green-100 p-3 rounded-full">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 hover:border-emerald-300 transition-colors">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Catering Leads</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.new_catering_inquiries || 0}</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-full">
                  <MessageSquare className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for different sections */}
        <Tabs defaultValue="orders" className="space-y-6">
          <TabsList className="grid w-full max-w-4xl grid-cols-2 sm:grid-cols-5 h-auto p-1 bg-stone-100">
            <TabsTrigger value="orders" className="text-sm py-3 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              <Package className="h-4 w-4 mr-2" />
              Orders
            </TabsTrigger>
            <TabsTrigger value="menu" className="text-sm py-3 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              <Utensils className="h-4 w-4 mr-2" />
              Menu
            </TabsTrigger>
            <TabsTrigger value="catering" className="text-sm py-3 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              <CalendarCheck className="h-4 w-4 mr-2" />
              Catering
            </TabsTrigger>
            <TabsTrigger value="food-truck" data-testid="tab-food-truck" className="text-sm py-3 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              <Truck className="h-4 w-4 mr-2" />
              Food Truck
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics" className="text-sm py-3 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              <BarChart3 className="h-4 w-4 mr-2" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Recent Orders</CardTitle>
                  <div className="flex gap-2">
                    <select
                      value={orderFilter.status}
                      onChange={(e) => setOrderFilter({...orderFilter, status: e.target.value})}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="preparing">Preparing</option>
                      <option value="ready">Ready</option>
                      <option value="delivered">Delivered</option>
                      <option value="picked_up">Picked Up</option>
                      <option value="refunded">Refunded</option>
                      <option value="partial_refund">Partial Refund</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                    <Input
                      type="date"
                      value={orderFilter.date}
                      onChange={(e) => setOrderFilter({...orderFilter, date: e.target.value})}
                      className="w-40"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {orders.length === 0 ? (
                    <p className="text-gray-600 text-center py-8">No orders yet</p>
                  ) : (
                    orders
                      .filter(order => {
                        if (orderFilter.status && order.status !== orderFilter.status) return false;
                        if (orderFilter.date && !order.scheduled_date.startsWith(orderFilter.date)) return false;
                        return true;
                      })
                      .map((order) => (
                      <Card key={order.id} className="border-2">
                        <CardContent className="p-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <p className="font-bold text-lg">{order.order_number}</p>
                              <p className="text-gray-600">{order.customer_name} • {order.customer_phone}</p>
                              <p className="text-sm text-gray-500">
                                {order.fulfillment_type === "delivery"
                                  ? "Delivery"
                                  : order.fulfillment_type === "food_truck_pickup"
                                  ? "Food Truck Pre-Order"
                                  : "Pickup"} • 
                                {new Date(order.scheduled_date).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-emerald-600">${order.total.toFixed(2)}</p>
                              <Badge className={statusColors[order.status] || "bg-gray-100 text-gray-800"}>
                                {order.status.replace('_', ' ')}
                              </Badge>
                            </div>
                          </div>

                          <div className="mb-4">
                            <p className="font-semibold mb-2">Items:</p>
                            {order.items.map((item, idx) => (
                              <p key={`${order.id}-${item.menu_item_id || idx}-${idx}`} className="text-gray-700">
                                {item.quantity}x {item.name}
                              </p>
                            ))}
                          </div>

                          <div className="flex gap-2 flex-wrap">
                            {order.status === "pending" && (
                              <Button
                                size="sm"
                                onClick={() => updateOrderStatus(order.id, "confirmed")}
                                className="bg-blue-600 hover:bg-blue-700"
                              >
                                Confirm
                              </Button>
                            )}
                            {order.status === "confirmed" && (
                              <Button
                                size="sm"
                                onClick={() => updateOrderStatus(order.id, "preparing")}
                                className="bg-purple-600 hover:bg-purple-700"
                              >
                                Start Preparing
                              </Button>
                            )}
                            {order.status === "preparing" && (
                              <Button
                                size="sm"
                                onClick={() => updateOrderStatus(order.id, "ready")}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                Mark Ready
                              </Button>
                            )}
                            {order.status === "ready" && order.fulfillment_type === "delivery" && (
                              <Button
                                size="sm"
                                onClick={() => updateOrderStatus(order.id, "out_for_delivery")}
                                className="bg-orange-600 hover:bg-orange-700"
                              >
                                Out for Delivery
                              </Button>
                            )}
                            {order.status === "out_for_delivery" && (
                              <Button
                                size="sm"
                                onClick={() => updateOrderStatus(order.id, "delivered")}
                                className="bg-emerald-600 hover:bg-emerald-700"
                              >
                                Mark Delivered
                              </Button>
                            )}
                            {order.status === "ready" && order.fulfillment_type === "pickup" && (
                              <Button
                                size="sm"
                                onClick={() => updateOrderStatus(order.id, "picked_up")}
                                className="bg-emerald-600 hover:bg-emerald-700"
                              >
                                Mark Picked Up
                              </Button>
                            )}
                            {order.payment_id && (
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid={`sync-square-${order.id}`}
                                onClick={() => syncSquareRefunds(order.id)}
                                className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                              >
                                Sync with Square
                              </Button>
                            )}
                          </div>

                          {(order.status === "refunded" || order.status === "partial_refund") && (
                            <div
                              data-testid={`refund-info-${order.id}`}
                              className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md"
                            >
                              <p className="font-semibold text-red-800">
                                {order.status === "refunded" ? "Fully Refunded" : "Partially Refunded"}
                                {typeof order.total_refunded_cents === "number" &&
                                  ` — $${(order.total_refunded_cents / 100).toFixed(2)}`}
                              </p>
                              {Array.isArray(order.refunds) && order.refunds.length > 0 && (
                                <ul className="text-xs text-red-700 mt-1 space-y-0.5">
                                  {order.refunds.map((r) => (
                                    <li key={r.refund_id}>
                                      ${((r.amount_cents || 0) / 100).toFixed(2)} • {r.status}
                                      {r.reason ? ` • ${r.reason}` : ""}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Menu Tab */}
          <TabsContent value="menu">
            <Card>
              <CardHeader>
                <CardTitle>Menu Items ({menuItems.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <MenuItemManager
                  menuItems={menuItems}
                  onChanged={fetchDashboardData}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pop-Up Events Tab */}
          <TabsContent value="events">
            <PopUpEventManager />
          </TabsContent>

          {/* Catering Tab */}
          <TabsContent value="catering">
            <Card>
              <CardHeader>
                <CardTitle>Catering Inquiries</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {cateringInquiries.length === 0 ? (
                    <p className="text-gray-600 text-center py-8">No catering inquiries yet</p>
                  ) : (
                    cateringInquiries.map((inquiry) => (
                      <Card key={inquiry.id} className="border-2">
                        <CardContent className="p-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <p className="font-bold text-lg">{inquiry.customer_name}</p>
                              <p className="text-gray-600">{inquiry.customer_email} • {inquiry.customer_phone}</p>
                              <p className="text-sm text-gray-500 mt-2">
                                <strong>Event:</strong> {inquiry.event_type} • {inquiry.guest_count} guests
                              </p>
                              <p className="text-sm text-gray-500">
                                <strong>Date:</strong> {new Date(inquiry.event_date).toLocaleDateString()}
                              </p>
                              <p className="text-sm text-gray-500">
                                <strong>Location:</strong> {inquiry.location}
                              </p>
                            </div>
                            <Badge className={
                              inquiry.status === "new" ? "bg-yellow-100 text-yellow-800" :
                              inquiry.status === "contacted" ? "bg-blue-100 text-blue-800" :
                              inquiry.status === "quoted" ? "bg-purple-100 text-purple-800" :
                              inquiry.status === "booked" ? "bg-green-100 text-green-800" :
                              "bg-gray-100 text-gray-800"
                            }>
                              {inquiry.status}
                            </Badge>
                          </div>

                          {inquiry.menu_preferences && (
                            <p className="text-sm text-gray-700 mb-2">
                              <strong>Preferences:</strong> {inquiry.menu_preferences}
                            </p>
                          )}

                          <div className="flex gap-2 flex-wrap mt-4">
                            {inquiry.status === "new" && (
                              <Button
                                size="sm"
                                onClick={() => updateCateringStatus(inquiry.id, "contacted")}
                                className="bg-blue-600 hover:bg-blue-700"
                              >
                                Mark Contacted
                              </Button>
                            )}
                            {inquiry.status === "contacted" && (
                              <Button
                                size="sm"
                                onClick={() => updateCateringStatus(inquiry.id, "quoted")}
                                className="bg-purple-600 hover:bg-purple-700"
                              >
                                Send Quote
                              </Button>
                            )}
                            {inquiry.status === "quoted" && (
                              <Button
                                size="sm"
                                onClick={() => updateCateringStatus(inquiry.id, "booked")}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                Mark Booked
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Food Truck Schedule Tab */}
          <TabsContent value="food-truck">
            <FoodTruckScheduleManager />
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <AnalyticsPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
